import { Button } from '@/components/ui/button';
import { Loader2, Save, AlertCircle, Eye, Building2, Mail, CheckCircle2 } from 'lucide-react';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { useAISettings } from './ai/hooks/useAISettings';
import { AIActivationSection } from './ai/AIActivationSection';
import { AIOperatingModeSection } from './ai/AIOperatingModeSection';
import { AIBusinessHoursSection } from './ai/AIBusinessHoursSection';
import { AIPersonalitySection } from './ai/AIPersonalitySection';
import { AIServiceTimingSection } from './ai/AIServiceTimingSection';
import { ChatAnalysisSection } from './ai/ChatAnalysisSection';
import { useClinica } from '@/contexts/ClinicaContext';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useState, useEffect } from 'react';

/**
 * O que faz: Formulário Principal de Configurações da IA
 * Onde usa: ConfiguracoesIAPage, Admin Panel
 * Conexões: useAISettings, useClinica, usePlanPermissions
 * 
 * REFATORADO: Layout premium com melhor UX
 * - Header da clínica integrado com visual moderno
 * - Grid responsivo para seções (2 colunas em desktop)
 * - Botão de salvar com feedback visual
 * - Modo view-only para planos Basic/Free
 */

export const AISettingsForm = () => {
  const {
    clinicaAtiva,
    isLoading: clinicaContextLoading,
    hasClinica,
    error: clinicaError
  } = useClinica();
  const {
    canConfigureAI,
    canViewAISettings,
    canUseChatAnalysis,
    analysisQuotaPerMonth,
    planName,
    isAdmin
  } = usePlanPermissions();
  const {
    settings,
    updateSetting,
    handleSave,
    isLoading,
    error,
    isSaving,
    queryClient
  } = useAISettings();

  // Estado para feedback de sucesso
  const [showSuccess, setShowSuccess] = useState(false);

  // Determinar se está em modo view-only
  const isViewOnly = canViewAISettings && !canConfigureAI && !isAdmin;

  // Handler para salvar com feedback
  const handleSaveWithFeedback = async () => {
    await handleSave();
    setShowSuccess(true);
  };

  // Auto-hide do sucesso após 3 segundos
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // =========================================================
  // ESTADO: CARREGANDO CLÍNICA
  // =========================================================
  if (clinicaContextLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <ThinkingOrbLoader state="composing" size={64} label="Carregando dados da clínica..." />
      </div>
    );
  }

  // =========================================================
  // ESTADO: ERRO NA CLÍNICA
  // =========================================================
  if (clinicaError || !hasClinica || !clinicaAtiva) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-danger-subtle flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-danger-text" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Erro ao carregar dados da clínica
          </h3>
          <p className="text-muted-foreground mb-6">
            {clinicaError?.message || 'Não foi possível encontrar os dados da sua clínica.'}
          </p>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="min-h-[44px]"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  // =========================================================
  // ESTADO: ERRO NAS CONFIGURAÇÕES
  // =========================================================
  if (error) {
    console.error('[AISettingsForm] Erro no carregamento:', error);
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-danger-subtle flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-danger-text" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Erro ao carregar configurações
          </h3>
          <p className="text-muted-foreground mb-6">{error.message}</p>
          <Button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['clinica-ai-settings'] })}
            variant="outline"
            className="min-h-[44px]"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  // =========================================================
  // RENDERIZAÇÃO PRINCIPAL
  // =========================================================
  return (
    <div className="space-y-6">
      {/* ===== HEADER: Informação da Clínica + Botão Salvar ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl bg-muted border border-border/50">
        {/* Info da Clínica */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-accent-foreground" />
          </div>
          <div>
            <p className="text-xs font-medium text-accent-foreground uppercase tracking-wider mb-0.5">
              Configurando para
            </p>
            <h3 className="font-semibold text-foreground text-lg">
              {clinicaAtiva.nome}
            </h3>
            {clinicaAtiva.email && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <Mail className="w-3.5 h-3.5" />
                {clinicaAtiva.email}
              </p>
            )}
          </div>
        </div>

        {/* Botão Salvar - Desktop */}
        {!isViewOnly && (
          <Button
            onClick={handleSaveWithFeedback}
            disabled={isSaving}
            className="hidden sm:flex min-h-[44px] px-6 rounded-full transition-all duration-200"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : showSuccess ? (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {showSuccess ? 'Salvo!' : 'Salvar alterações'}
          </Button>
        )}
      </div>

      {/* ===== AVISO: Modo View-Only ===== */}
      {isViewOnly && (
        <div className="flex items-start gap-4 p-4 sm:p-5 rounded-2xl bg-warning-subtle border border-warning/30">
          <div className="w-10 h-10 rounded-xl bg-warning-subtle flex items-center justify-center flex-shrink-0">
            <Eye className="h-5 w-5 text-warning-text" />
          </div>
          <div>
            <h4 className="font-semibold text-warning-text mb-1">
              Modo Visualização
            </h4>
            <p className="text-sm text-warning-text">
              Seu plano <strong className="font-semibold">{planName.toUpperCase()}</strong> permite visualizar, mas não editar.
              Faça upgrade para o plano Professional para desbloquear a edição.
            </p>
          </div>
        </div>
      )}

      {/* ===== LOADING: Configurações ===== */}
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center gap-3 px-4 py-2 bg-muted rounded-full">
            <Loader2 className="h-4 w-4 animate-spin text-accent-foreground" />
            <span className="text-sm text-muted-foreground">Carregando configurações...</span>
          </div>
        </div>
      )}

      {/* ===== GRID DE SEÇÕES ===== */}
      {/* 
       * Layout: 
       * - Mobile: 1 coluna, todas as seções empilhadas
       * - Desktop (xl+): 2 colunas para seções menores, 1 coluna para personalidade
       */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        {/* Coluna 1: Ativação + Modo de Operação */}
        <div className="space-y-4 sm:space-y-6">
          <AIActivationSection settings={settings} updateSetting={updateSetting} readOnly={isViewOnly} />
          <AIOperatingModeSection settings={settings} updateSetting={updateSetting} readOnly={isViewOnly} />
        </div>

        {/* Coluna 2: Horários + Tempos de Procedimentos */}
        <div className="space-y-4 sm:space-y-6">
          <AIBusinessHoursSection settings={settings} updateSetting={updateSetting} readOnly={isViewOnly} />
          <AIServiceTimingSection readOnly={isViewOnly} />
        </div>
      </div>

      {/* Personalidade - Full Width (seção grande com textareas) */}
      <AIPersonalitySection settings={settings} updateSetting={updateSetting} readOnly={isViewOnly} />

      {/* Análise Diária de Chats — Premium + Admin */}
      {clinicaAtiva?.id && (
        <ChatAnalysisSection
          clinicaId={clinicaAtiva.id}
          readOnly={isViewOnly}
          canUse={canUseChatAnalysis}
          analysisQuotaPerMonth={analysisQuotaPerMonth}
        />
      )}

      {/* ===== BOTÃO SALVAR: Mobile (fixed bottom) + Desktop (inline) ===== */}
      {!isViewOnly && (
        <>
          {/* Mobile: Botão fixo no rodapé */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-card/80 backdrop-blur-lg border-t border-border sm:hidden z-50">
            <Button
              onClick={handleSaveWithFeedback}
              disabled={isSaving}
              className="w-full min-h-[48px] rounded-full"
            >
              {isSaving ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : showSuccess ? (
                <CheckCircle2 className="h-5 w-5 mr-2" />
              ) : (
                <Save className="h-5 w-5 mr-2" />
              )}
              {showSuccess ? 'Alterações salvas!' : 'Salvar alterações'}
            </Button>
          </div>

          {/* Desktop: Botão inline no final */}
          <div className="hidden sm:flex justify-end pt-4">
            <Button
              onClick={handleSaveWithFeedback}
              disabled={isSaving}
              className="min-h-[44px] px-8 rounded-full"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : showSuccess ? (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {showSuccess ? 'Alterações salvas!' : 'Salvar todas as alterações'}
            </Button>
          </div>

          {/* Spacer para não cobrir conteúdo no mobile com o botão fixo */}
          <div className="h-20 sm:hidden" />
        </>
      )}
    </div>
  );
};